import { createSelector } from "@reduxjs/toolkit";
import { type PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse } from "../../../../../../../openapi";
import { azureBlobStorageRTK } from "../../../../../azureBlobStorageRTK";
import {
  selectCurrentOpenIssueBoardInModal,
  selectCurrentOpenIssueInModal,
  selectCurrentOpenIssueModal,
} from "../../../issueSelectors";
import { IRootState } from "../../../../../../store";

export type TAiAssessment = {
  verdict: "PASS" | "PARTIAL" | "FAIL" | "UNCERTAIN" | null;
  summary: string;
  supportingPoints: string[];
  gaps: string[];
};

const verdictColorMap: Record<PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse["verdict"], string> = {
  PASS: "green",
  PARTIAL: "yellow",
  FAIL: "red",
  UNCERTAIN: "gray",
};

export const parseAiAssessment = (rawText?: string): TAiAssessment => {
  if (!rawText?.trim()) {
    return {
      verdict: null,
      summary: "",
      supportingPoints: [],
      gaps: [],
    };
  }

  const text = rawText.replace(/\r\n/g, "\n").trim();

  const verdictMatch = text.match(/Verdict:\s*(PASS|PARTIAL|FAIL|UNCERTAIN)/i);
  const verdict = (verdictMatch?.[1]?.toUpperCase() as TAiAssessment["verdict"]) ?? null;

  const extractSection = (sectionName: string, nextSectionNames: string[]) => {
    const nextPattern = nextSectionNames.length > 0 ? `(?=\\n(?:${nextSectionNames.join("|")}):)` : "$";
    const regex = new RegExp(`${sectionName}:\\s*\\n([\\s\\S]*?)${nextPattern}`, "i");
    const match = text.match(regex);
    return match?.[1]?.trim() ?? "";
  };

  const parseBulletList = (input: string) =>
    input
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => line.replace(/^-+\s*/, "").trim())
      .filter(Boolean);

  return {
    verdict,
    summary: extractSection("Summary", ["Supporting points", "Gaps", "Verdict"]),
    supportingPoints: parseBulletList(extractSection("Supporting points", ["Gaps", "Verdict", "Summary"])),
    gaps: parseBulletList(extractSection("Gaps", ["Supporting points", "Verdict", "Summary"])),
  };
};

const EMPTY_ISSUE_FILES_QUERY = {
  data: [],
  isUninitialized: true,
  isLoading: false,
};

export const selectBreeamIssueFilesPath = (projectId: string, issueId: string) => `${projectId}/issue/${issueId}/files`;

export const selectBreeamIssueFilesQueryArgs = createSelector([selectCurrentOpenIssueModal], (modal) => {
  if (modal?.projectId == null || modal.itemId == null) return null;

  return {
    projectId: modal.projectId,
    path: selectBreeamIssueFilesPath(modal.projectId, modal.itemId),
  };
});

export const selectBreeamRunArgs = createSelector([selectCurrentOpenIssueModal], (modal) => {
  if (modal?.projectId == null || modal.itemId == null) return null;

  return {
    projectId: modal.projectId,
    issueId: modal.itemId,
    body: {},
  };
});

export const selectBreeamModalHeader = createSelector(
  [selectCurrentOpenIssueInModal, selectCurrentOpenIssueBoardInModal],
  (issue, issueBoard) => ({
    issueId: issue?.id ?? "",
    issueTitle: issue?.title ?? "",
    inputAttributeLabel: issueBoard?.otherAttributesConfig?.I?.label ?? "Tekst til BRE online tool",
  }),
);

export const selectBreeamIssueFilesQuery = (state: IRootState) => {
  const queryArgs = selectBreeamIssueFilesQueryArgs(state);

  if (queryArgs == null) {
    return EMPTY_ISSUE_FILES_QUERY;
  }

  return azureBlobStorageRTK.endpoints.getFilesFromAzureBlobStorage.select(queryArgs)(state);
};

const selectIssuePdfFiles = createSelector([selectBreeamIssueFilesQuery], (issueFilesQuery) =>
  (issueFilesQuery.data ?? []).filter((file) => file.fileName?.toLowerCase().endsWith(".pdf")),
);

const selectBreeamGuidanceText = createSelector([selectCurrentOpenIssueInModal], (issue) => {
  const rawGuidanceText = issue?.otherAttributesData?.I;

  return typeof rawGuidanceText === "string"
    ? rawGuidanceText
    : rawGuidanceText == null
      ? ""
      : String(rawGuidanceText);
});

const selectIsCheckingPrerequisites = createSelector(
  [selectBreeamIssueFilesQuery],
  (issueFilesQuery) => issueFilesQuery.isUninitialized === true || issueFilesQuery.isLoading === true,
);

const selectMissingPrerequisites = createSelector(
  [selectBreeamGuidanceText, selectBreeamIssueFilesQuery, selectIssuePdfFiles],
  (guidanceText, issueFilesQuery, issuePdfFiles) => {
    const missing: string[] = [];

    if (guidanceText.trim().length === 0) {
      missing.push("Veiledningstekst fra manualen mangler.");
    }

    if (issueFilesQuery.isUninitialized === false && issueFilesQuery.isLoading === false && issuePdfFiles.length === 0) {
      missing.push("Legg til minst én PDF-fil på saken før BREEAM-sjekken kjøres.");
    }

    return missing;
  },
);

export const selectBreeamPrerequisites = createSelector(
  [selectMissingPrerequisites, selectIsCheckingPrerequisites],
  (missingPrerequisites, isCheckingPrerequisites) => ({
    missingPrerequisites,
    isCheckingPrerequisites,
    canRunCheck: missingPrerequisites.length === 0 && isCheckingPrerequisites === false,
  }),
);

export const selectParsedAiAssessment = (result: PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse | null) =>
  parseAiAssessment(result?.analysis?.[0]);

export const selectEffectiveVerdict = (args: {
  parsedAiAssessment: TAiAssessment;
  result: PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse | null;
}): PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse["verdict"] =>
  args.parsedAiAssessment.verdict ?? args.result?.verdict ?? "UNCERTAIN";

export const selectVerdictColor = (verdict: PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse["verdict"]) =>
  verdictColorMap[verdict];

export const selectTechnicalAnalysis = (result: PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse | null) =>
  result?.analysis.slice(1) ?? [];

export const selectCopyableAiAssessmentText = (result: PostProjectByProjectIdIssuesAndIssueIdRunBreeamCheckApiResponse | null) =>
  result?.analysis[0] ?? "";
